import { useBackNavigation } from "@/hooks/useBackNavigation.ts";
import { useCallback } from "react";
import { isFloatingLayerOpen } from "./floatingLayers.ts";
import { useShortcut } from "./useShortcut.ts";

/**
 * Screen-level Escape-to-back: registers the `system.back` shortcut and returns
 * the back handler so a header button can share the exact same behavior. `onBack`
 * may veto navigation by returning `false`.
 *
 * `enabled` exists because `useShortcut` binds per call site: a screen that
 * drops its back button still wants the key, so it registers the gesture itself
 * and turns the binding off wherever the button also renders.
 */
export const useEscapeToBack = (onBack?: () => boolean, enabled = true): (() => void) => {
  const navigateBack = useBackNavigation();

  const handleBack = useCallback(() => {
    if (!onBack || onBack()) navigateBack();
  }, [onBack, navigateBack]);

  // Defer to any Radix layer that is on screen — see `isFloatingLayerOpen` for
  // why the check is a DOM query and what it matches.
  useShortcut(
    "system.back",
    () => {
      if (isFloatingLayerOpen()) return;
      handleBack();
    },
    { deps: [handleBack], enabled },
  );

  return handleBack;
};
